import React from 'react';

/**
 * Template "manche-bilan" — le résultat d'une manche de l'usine, redessiné dans
 * la langue visuelle de la conférence.
 *
 * POURQUOI IL EXISTE. La simulation a son propre bilan : une pop-in noire
 * (`#summary` dans `public/usine/index.html`) posée sur un fond assombri. Elle
 * est juste, mais elle est écrite dans la typographie du jeu — panneau
 * quasi-noir, capitales espacées, barres lime et rouge — et elle tombait au
 * milieu d'un talk qui a la sienne. Sur vidéoprojecteur, l'écran entier
 * s'assombrissait le temps du bilan, puis redevenait clair à la slide suivante.
 * Ces trois slides remplacent les trois pop-ins : même information, même
 * chiffres, mais le regard ne change plus de monde.
 *
 * CE QUE LA SLIDE DOIT FAIRE VOIR EN UNE SECONDE, et c'est l'histogramme qui
 * s'en charge — pas les totaux :
 *   · manche I   : cinq barres égales. « Ça tient. »
 *   · manche II  : deux barres pleines, puis la chute au tour 3.
 *   · manche III : quatre barres plates, puis une haute. Le gain n'arrive qu'au
 *                  dernier tour — c'est l'argument contre l'abandon des pilotes,
 *                  et la courbe en J de la slide suivante le généralise.
 * Les barres des trois manches sont à la MÊME ÉCHELLE (le maximum est calculé
 * sur toutes les manches affichées) : sans ça, comparer deux manches côte à côte
 * n'aurait aucun sens, et c'est exactement ce que la slide vend.
 *
 * ⚠️ LES CHIFFRES SONT ÉCRITS EN DUR DANS LES DONNÉES DE SLIDE, et c'est un
 * compromis assumé. Ils sont déterministes — mêmes préréglages, mêmes tours,
 * même moteur — et ils ont été relevés en rejouant les trois manches
 * (`LB.sim.create()` puis `resolveTurn()` en boucle). Mais ils ne se
 * recalculent pas : si un rendement, un préréglage ou une règle bouge dans
 * `public/usine/src/config/rounds.js`, CES SLIDES MENTENT sans que rien ne le
 * signale. Le jour où l'équilibrage rebouge, rejoue les trois manches et
 * reporte les valeurs. La règle « aucun chiffre en dur » du moteur ne s'applique
 * pas ici : une slide n'a pas accès à l'iframe.
 *
 * Champs de slide :
 *  - title, subtitle : comme partout
 *  - manches : [{ roman, label, tours: [n…], total, parTour, dette, ton }]
 *      · `tours`   les valeurs livrées tour par tour — ce sont elles, le sujet
 *      · `ton`     "neutre" | "chute" | "gain" — colore le totaliseur
 *      · `enVert` (optionnel) indices des tours passés en vert, valeur et barre.
 *        Déclaré à la main : une règle « la barre monte » colorerait aussi le
 *        3,5 → 4 de la manche II, qui n'est pas un gain.
 *      · `detteDepart` (optionnel) la dette AVANT la manche. Fournie, elle fait
 *        afficher l'opération — `32 +5 = 37` — au lieu du seul résultat. Ignorée
 *        dès qu'une slide affiche plus d'une manche : voir BlocDette.
 *  - detteMax : plafond de l'échelle de dette (80 = RULES.DEBT_MAX)
 *  - note : le verdict, en bas, en petit
 */
/**
 * LA DETTE D'UNE MANCHE, ET SON ÉVOLUTION.
 *
 * ⚠️ C'ÉTAIT UN CHIFFRE DE FIN — « dette 37 », en petit, dans un coin. Sur la
 * slide « La référence » c'est un défaut de fond : cette manche est justement
 * celle où il ne se passe rien, et la dette est la SEULE chose qui y bouge.
 * Elle monte d'un point par tour, tranquillement, pendant que tout va bien —
 * et c'est le +20 de la manche II qui, ensuite, doit se comparer à ce +5. Un
 * résultat ne se compare pas ; une opération, si.
 *
 * D'où la forme longue quand `detteDepart` est fourni : `32 +5 = 37`, le delta
 * en rouge quand elle monte, en cyan quand elle redescend (manche III). Sans
 * `detteDepart`, on garde la forme courte d'origine : les deux slides de
 * récapitulatif empilent deux et trois manches dans la même hauteur, et une
 * ligne de plus par manche y passerait sous la barre de navigation — c'est un
 * débordement qui s'est déjà produit, voir la note sur le budget vertical.
 */
const BlocDette = ({ manche, detteMax, couleur, detaille }) => {
    const delta = manche.detteDepart != null ? manche.dette - manche.detteDepart : null;
    const jauge = (
        <div className="w-20 h-1.5 rounded-full bg-slate-700/60 overflow-hidden">
            <div className={`h-full rounded-full ${couleur(manche.dette)}`}
                style={{ width: `${Math.min(100, (manche.dette / detteMax) * 100)}%` }} />
        </div>
    );

    if (delta == null || !detaille) {
        return (
            <div className="mt-2 flex items-center justify-end gap-2">
                {jauge}
                <span className="text-slate-400/80 font-mono"
                    style={{ fontSize: 'clamp(0.68rem, min(1vw, 1.8vh), 1rem)' }}>
                    dette {manche.dette}
                </span>
            </div>
        );
    }

    /* La jauge prend toute la largeur de la colonne : à 80 px, sous des chiffres
       deux fois plus hauts, elle ne se lisait plus comme une échelle mais comme
       un reste d'affichage. */
    const monte = delta >= 0;
    return (
        <div className="mt-3 flex flex-col items-end gap-2">
            <p className="font-mono tracking-[0.2em] text-slate-500 leading-none"
                style={{ fontSize: 'clamp(0.6rem, min(0.95vw, 1.7vh), 0.9rem)' }}>
                DETTE TECHNIQUE
            </p>
            <p className="font-mono leading-none whitespace-nowrap"
                style={{ fontSize: 'clamp(1.1rem, min(2.2vw, 3.9vh), 2.2rem)' }}>
                <span className="text-slate-400">{manche.detteDepart}</span>
                <span className={`font-black ${monte ? 'text-rose-400' : 'text-cyan-300'}`}>
                    {' '}{monte ? '+' : '−'}{Math.abs(delta)}{' '}
                </span>
                <span className="text-slate-600">=</span>
                <span className="text-slate-100 font-black"> {manche.dette}</span>
            </p>
            <div className="w-full h-1.5 rounded-full bg-slate-700/60 overflow-hidden">
                <div className={`h-full rounded-full ${couleur(manche.dette)}`}
                    style={{ width: `${Math.min(100, (manche.dette / detteMax) * 100)}%` }} />
            </div>
        </div>
    );
};

const MancheBilanSlide = ({ slide, slideKey }) => {
    const manches = slide.manches || [];
    const detteMax = slide.detteMax || 80;

    /* Échelle commune à toutes les manches affichées. Le plancher à 1 évite une
       division par zéro sur une manche qui n'aurait rien livré. */
    const pic = Math.max(1, ...manches.flatMap((m) => m.tours || []));

    const tons = {
        chute: 'text-rose-300',
        gain: 'text-cyan-300',
        neutre: 'text-blue-100'
    };

    /* ⚠️ LE BUDGET VERTICAL EST LA SEULE VRAIE CONTRAINTE DE CETTE SLIDE, et il
       se resserre à chaque manche ajoutée : la troisième doit tenir dans la même
       hauteur que la première. Les barres sont donc dimensionnées d'après le
       NOMBRE DE LIGNES, pas d'après la fenêtre seule.
       Ce qui arrive quand on se trompe, et c'est arrivé : le bloc est centré
       verticalement, donc un contenu trop haut ne déborde pas « en bas » — il
       déborde des DEUX côtés. Le titre passe alors sous le bandeau de chapitre
       (« L'USINE ») et la dernière manche passe sous la barre de navigation.
       D'où aussi l'`overflow-hidden` du conteneur : même mal réglé, rien ne peut
       plus remonter dans l'en-tête. */
    const hauteurBarres = manches.length >= 3 ? 'clamp(42px, 9vh, 96px)'
        : manches.length === 2 ? 'clamp(48px, 11vh, 116px)'
            : 'clamp(54px, 13vh, 140px)';
    const serre = manches.length >= 3;
    /* La forme longue de la dette n'est servie que sur une slide à une seule
       manche : ailleurs, la place verticale n'existe pas (voir BlocDette). */
    const detteDetaillee = manches.length === 1 && manches.some((m) => m.detteDepart != null);

    /* La dette n'a pas de seuil « bon / mauvais » dans le moteur : elle a un
       plafond de blocage. On colore donc par proximité du plafond, pas par
       jugement — deux tiers du plafond, c'est là que la machine commence à
       refroidir (voir DEBT_CHILL dans rounds.js). */
    const couleurDette = (d) =>
        d >= detteMax * 0.66 ? 'bg-rose-500/70'
            : d >= detteMax * 0.5 ? 'bg-amber-400/70'
                : 'bg-blue-400/50';

    /* `pb-14` est volontairement plus grand que `pt-6` : le bloc est centré
       verticalement, et la barre de navigation du diaporama mord sur le bas de la
       zone utile. Sans ce déséquilibre, la troisième manche finissait à neuf
       pixels des flèches. */
    return (
        <div key={slideKey} className="h-full px-12 pt-6 pb-14 flex flex-col justify-center relative overflow-hidden">
            <div className="relative z-10 w-full max-w-6xl mx-auto min-h-0 flex flex-col gap-[2.2vh]">
                <div className="text-center">
                    <h2
                        className="font-bold pb-2 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up leading-tight"
                        style={{ fontSize: 'clamp(1.35rem, min(2.8vw, 5vh), 2.75rem)' }}
                    >
                        {slide.title}
                    </h2>
                    {slide.subtitle && (
                        <p
                            className="text-blue-200/60 animate-fade-in-up stagger-1 mt-2"
                            style={{ fontSize: 'clamp(0.8rem, min(1.25vw, 2.2vh), 1.25rem)' }}
                        >
                            {slide.subtitle}
                        </p>
                    )}
                </div>

                <div className={`flex flex-col ${serre ? 'gap-2.5' : 'gap-4'}`}>
                    {manches.map((m, i) => (
                        <div
                            key={i}
                            className={`bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-500/20 rounded-2xl flex items-center animate-fade-in-up ${serre ? 'px-6 py-3 gap-6' : 'px-8 py-5 gap-8'}`}
                            style={{ animationDelay: `${0.15 + i * 0.15}s`, opacity: 0 }}
                        >
                            {/* L'identité de la manche. Largeur fixe : les trois
                                étiquettes doivent s'aligner d'une ligne à l'autre,
                                sinon les histogrammes ne démarrent pas au même x
                                et la comparaison visuelle est faussée. */}
                            <div className="w-44 shrink-0">
                                <p className="font-mono tracking-widest text-cyan-300/90"
                                    style={{ fontSize: 'clamp(0.8rem, min(1.3vw, 2.4vh), 1.35rem)' }}>
                                    MANCHE {m.roman}
                                </p>
                                <p className="text-blue-200/50"
                                    style={{ fontSize: 'clamp(0.7rem, min(1.1vw, 2vh), 1.1rem)' }}>
                                    {m.label}
                                </p>
                            </div>

                            {/* L'histogramme — le cœur de la slide. Une barre par
                                tour, à l'échelle commune. Une barre qui livre moins
                                que la précédente passe en rose : c'est la règle de
                                lecture de la simulation elle-même, on la garde pour
                                que la salle retrouve ce qu'elle vient de voir. */}
                            <div className="flex-1 flex items-end gap-2" style={{ height: hauteurBarres }}>
                                {(m.tours || []).map((v, k) => {
                                    const baisse = k > 0 && v < m.tours[k - 1] - 0.01;
                                    const vert = (m.enVert || []).includes(k);
                                    return (
                                        <div key={k} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                                            <span
                                                className={`font-mono ${baisse ? 'text-rose-300' : vert ? 'text-emerald-400 font-bold' : 'text-blue-200/70'}`}
                                                style={{ fontSize: 'clamp(0.75rem, min(1.15vw, 2vh), 1.15rem)' }}
                                            >
                                                {String(v).replace('.', ',')}
                                            </span>
                                            <div
                                                className={`w-full rounded-t ${baisse
                                                    ? 'bg-gradient-to-t from-rose-600/60 to-rose-400'
                                                    : vert
                                                        ? 'bg-gradient-to-t from-emerald-600/60 to-emerald-400'
                                                        : 'bg-gradient-to-t from-blue-600/60 to-cyan-400'}`}
                                                style={{ height: `${Math.max(4, (v / pic) * 100)}%` }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Le totaliseur. Le débit PAR TOUR est collé au total
                                parce que c'est lui qui se compare : un total seul
                                laisserait croire qu'une manche plus longue produit
                                mieux — exactement le graphique malhonnête que la
                                conférence dénonce par ailleurs. */}
                            <div className={`shrink-0 text-right ${detteDetaillee ? 'w-64' : 'w-48'}`}>
                                <p className={`font-black leading-none ${tons[m.ton] || tons.neutre}`}
                                    style={{ fontSize: 'clamp(1.3rem, min(2.6vw, 5vh), 2.75rem)' }}>
                                    {m.total}
                                </p>
                                <p className="text-blue-200/60 font-mono mt-1"
                                    style={{ fontSize: 'clamp(0.75rem, min(1.15vw, 2vh), 1.15rem)' }}>
                                    {m.parTour} / tour
                                </p>
                                <BlocDette
                                    manche={m}
                                    detteMax={detteMax}
                                    couleur={couleurDette}
                                    detaille={detteDetaillee}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {slide.note && (
                    <p
                        className="text-blue-300/60 text-center leading-snug animate-fade-in-up stagger-4 max-w-4xl mx-auto"
                        style={{ fontSize: 'clamp(0.7rem, min(1.1vw, 2vh), 1.1rem)' }}
                    >
                        {slide.note}
                    </p>
                )}
            </div>
        </div>
    );
};

export default MancheBilanSlide;
